import React, { useState, useCallback, useContext } from "react";
import { DemandPlanEditsContext } from "./context";
import {
  PlannerOverrideInputs,
  FinalForecastInputs,
  YoYPercentageInputs,
} from "./type";
import { useApi, useCustomer, useWasm } from "../app/provider";
import {
  useSaveDemandPlanLayerToDefaultSettingsMutation,
  UserRunSettingsInput,
  GetMyDemandPlanLayerUploadUrlDocument,
  GetMyDemandPlanLayerDownloadUrlDocument,
} from "../../services/api/__generated__/backend_gateway-types";
import { VersionedDataContext } from "../versioned_data/context";
import { SysMessagesContext } from "../sys_messages/context";
import { SysMessageLevel } from "../sys_messages/type";
import { useLocation } from "react-router-dom";

import {
  convertDemandPlanInputsToParquet,
  demandPlanLayerKey,
  normalizeWindowEndToLastDay,
  DemandPlanLayerRow,
} from "../../utils/mui_table_utils";

import {
  Table as WasmTable,
  WriterPropertiesBuilder,
  Compression,
  writeParquet,
  readParquet,
} from "parquet-wasm";

import {
  makeBuilder,
  Utf8,
  List,
  Float64,
  Timestamp,
  TimeUnit,
  tableToIPC,
  tableFromIPC,
  Field,
  Table,
} from "apache-arrow";

// Parquet timestamp cell → epoch ms. apache-arrow surfaces Timestamp(us, UTC) as a
// bigint of MICROSECONDS, so divide by 1000; numbers (already ms) and Dates pass through.
const layerTsToMs = (value: unknown): number => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "bigint") return Number(value / BigInt(1000));
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  return new Date(value as string).getTime();
};

// Extract a List<Utf8> cell (group_level / group_value) into a string[]. Arrow
// surfaces a list cell as a sub-Vector / array-like; normalize to strings.
const toStringArray = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  const arr =
    typeof (value as any)?.toArray === "function"
      ? (value as any).toArray()
      : Array.from(value as Iterable<unknown>);
  return Array.from(arr as Iterable<unknown>).map((v) => String(v));
};

/**
 * Read the persisted demand_plan_layer.parquet back as rows so this session's save
 * can MERGE rather than overwrite. Returns [] when none has been saved yet (null URL
 * or 404).
 */
const downloadPriorLayerRows = async (
  client: any
): Promise<DemandPlanLayerRow[]> => {
  const result = await client.query({
    query: GetMyDemandPlanLayerDownloadUrlDocument,
    fetchPolicy: "no-cache",
  });
  const downloadUrl = result.data?.getMyDemandPlanLayerDownloadUrl;
  if (!downloadUrl) {
    // No prior layer (first-ever edit) — prior set is empty.
    return [];
  }

  const response = await fetch(downloadUrl);
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw new Error(
      `Failed to download prior demand plan layer: ${response.status} ${response.statusText}`
    );
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  const wasmTable = readParquet(bytes);
  const arrowTable = tableFromIPC(wasmTable.intoIPCStream());

  const rows: DemandPlanLayerRow[] = [];
  for (let i = 0; i < arrowTable.numRows; i++) {
    const startMs = layerTsToMs(arrowTable.getChild("start")?.get(i));
    rows.push({
      group_level: toStringArray(arrowTable.getChild("group_level")?.get(i)),
      group_value: toStringArray(arrowTable.getChild("group_value")?.get(i)),
      start: startMs,
      // Migrate a legacy first-of-next-period end to the inclusive last-day convention
      // so a previously-saved row keys identically to a fresh one (else duplicate row +
      // engine double-apply).
      end: normalizeWindowEndToLastDay(
        startMs,
        layerTsToMs(arrowTable.getChild("end")?.get(i))
      ),
      quantity_override: Number(
        arrowTable.getChild("quantity_override")?.get(i) ?? 0
      ),
      override_type: String(arrowTable.getChild("override_type")?.get(i) ?? ""),
      edited_by: String(arrowTable.getChild("edited_by")?.get(i) ?? ""),
      edited_at: layerTsToMs(arrowTable.getChild("edited_at")?.get(i)),
    });
  }
  return rows;
};

interface DemandPlanEditsProviderProps {
  children: React.ReactNode;
}

/**
 * Demand Plan persistent-edit context.
 *
 * Same edit UX as demand_walk_edits, but `saveEditsAndTriggerRun` PERSISTS the edit:
 * it reads the customer's current demand_plan_layer.parquet back, MERGES this session's
 * cells over it (session wins; cleared cells deleted; elapsed windows pruned), re-encodes
 * the full-state parquet, PUTs it to S3 via a presigned URL (bypassing the WAF body
 * limit), and calls saveDemandPlanLayerToDefaultSettings to write it + trigger a run.
 *
 * The read-back-merge is the correctness guarantee: a second browser session never drops
 * the first's overrides. A blank/"-" cell is a REVERT (deletes the prior override); a
 * first-ever save just starts from an empty map.
 */
export const DemandPlanEditsProvider: React.FC<DemandPlanEditsProviderProps> = ({
  children,
}) => {
  const { apiService } = useApi();
  const { wasmInitialized, wasmError } = useWasm();
  const { whoIAm } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    engineInProgress,
    justTriggeredRun,
    setJustTriggeredRun,
    loading: runsLoading,
  } = useContext(VersionedDataContext);
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { search } = useLocation();

  const [plannerOverrideInputs, setPlannerOverrideInputs] =
    useState<PlannerOverrideInputs>({});
  const [finalForecastInputs, setFinalForecastInputs] =
    useState<FinalForecastInputs>({});
  const [yoyPercentageInputs, setYoYPercentageInputs] =
    useState<YoYPercentageInputs>({});

  const [editMode, setEditModeState] = useState<boolean>(false);

  const [saveDemandPlanLayer, { loading }] =
    useSaveDemandPlanLayerToDefaultSettingsMutation({
      client: apiService.getClient(),
    });

  const setEditMode = useCallback((mode: boolean) => {
    setEditModeState(mode);
  }, []);

  const clearEdits = useCallback(() => {
    setPlannerOverrideInputs({});
    setFinalForecastInputs({});
    setYoYPercentageInputs({});
  }, []);

  const discardChanges = useCallback(() => {
    clearEdits();
    setEditModeState(false);
  }, [clearEdits]);

  const saveEditsAndTriggerRun = useCallback(
    async (runName: string) => {
      try {
        if (
          loading ||
          runsLoading ||
          engineInProgress ||
          justTriggeredRun !== null
        ) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message:
              "A run is already in progress. Please wait for it to complete.",
          });
          return;
        }

        const runToUse = mainRun || lastSuccessfulRun;
        if (!runToUse) {
          console.error("No run available for demand plan save");
          return;
        }

        if (!wasmInitialized) {
          throw new Error("WASM not initialized yet.");
        }
        if (wasmError) {
          throw new Error(`WASM initialization failed: ${wasmError}`);
        }

        // Optimistically grey out the run button (save does 3 network hops); rolled
        // back in catch on failure.
        setJustTriggeredRun({
          runSettings: { ...runToUse.runSettings, organizationId: "" },
          status: "RUN_IN_PROGRESS",
          createdAt: new Date().toISOString(),
          stepIds: "[]",
          updatedAt: new Date().toISOString(),
          initiatedBy: whoIAm?.email || null,
          name: null,
          description: null,
          displayId: null,
          atStep: 0,
          totalSteps: 1,
          id: "",
        });

        // ── Read segments + grain from the URL (set by the pivot controls). ──
        const urlParams = new URLSearchParams(search);
        const segmentsParam = urlParams.get("segments");
        if (!segmentsParam) {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message:
              "No segments in URL. Cannot save demand plan edits without segmentation info.",
          });
          setJustTriggeredRun(null);
          return;
        }
        let segments: string[];
        try {
          segments = JSON.parse(segmentsParam);
        } catch {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message: "Invalid segments format in URL. Cannot save demand plan edits.",
          });
          setJustTriggeredRun(null);
          return;
        }
        if (!segments || segments.length === 0) {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message: "Empty segments in URL. Cannot save demand plan edits.",
          });
          setJustTriggeredRun(null);
          return;
        }

        const measure = urlParams.get("measure") || "quantity";
        // date_field is order_placed_month | order_placed_week -> grain.
        const dateField = urlParams.get("date_field") || "order_placed_month";
        const grain: "month" | "week" = dateField.includes("week")
          ? "week"
          : "month";

        // ── Build the full-state layer rows for this session. ──
        const userEmail = whoIAm?.email || "unknown";
        const nowMs = Date.now();
        const { rows: sessionRows, clearedKeys } =
          convertDemandPlanInputsToParquet(
            finalForecastInputs,
            segments,
            measure,
            grain,
            userEmail,
            nowMs,
            addSystemMessage
          );

        // True no-op only when nothing to upsert AND nothing to revert. A pure revert
        // has clearedKeys, so it falls through and rewrites the merged layer without them.
        const hasLocalEdits = Object.keys(finalForecastInputs).length > 0;
        if (
          sessionRows.length === 0 &&
          clearedKeys.size === 0 &&
          !hasLocalEdits
        ) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: "No valid edits found to save. Please make some changes first.",
          });
          setJustTriggeredRun(null);
          return;
        }

        // ── Read-back-merge: seed from the prior persisted layer, upsert this session
        // (session wins), delete reverted cells, prune elapsed windows. This is why a
        // second session never drops the first's overrides. ──
        const apolloClient = apiService.getClient();
        const priorRows = await downloadPriorLayerRows(apolloClient);
        const mergedByKey = new Map<string, DemandPlanLayerRow>();
        const nowDayStart = new Date(
          Date.UTC(
            new Date(nowMs).getUTCFullYear(),
            new Date(nowMs).getUTCMonth(),
            new Date(nowMs).getUTCDate()
          )
        ).getTime();

        // Seed with prior rows (skip any already-elapsed window up front).
        for (const row of priorRows) {
          if (row.end <= nowDayStart) continue; // prune fully-elapsed
          mergedByKey.set(
            demandPlanLayerKey(
              row.group_level,
              row.group_value,
              row.start,
              row.end
            ),
            row
          );
        }
        // Upsert this session's edited cells — session value wins.
        for (const row of sessionRows) {
          mergedByKey.set(
            demandPlanLayerKey(
              row.group_level,
              row.group_value,
              row.start,
              row.end
            ),
            row
          );
        }
        // Revert: delete cells the planner cleared this session.
        for (const key of clearedKeys) {
          mergedByKey.delete(key);
        }

        const layerRows: DemandPlanLayerRow[] = Array.from(
          mergedByKey.values()
        );

        // ── Encode the parquet with the locked schema + audit columns. ──
        const listUtf8 = new List(new Field("item", new Utf8()));
        const tsUTCus = new Timestamp(TimeUnit.MICROSECOND, "UTC");

        const groupLevelB = makeBuilder({ type: listUtf8 });
        const groupValueB = makeBuilder({ type: listUtf8 });
        const startB = makeBuilder({ type: tsUTCus });
        const endB = makeBuilder({ type: tsUTCus });
        const qtyB = makeBuilder({ type: new Float64() });
        const typeB = makeBuilder({ type: new Utf8() });
        const editedByB = makeBuilder({ type: new Utf8() });
        const editedAtB = makeBuilder({ type: tsUTCus });

        for (const row of layerRows) {
          groupLevelB.append(row.group_level);
          groupValueB.append(row.group_value);
          startB.append(row.start);
          endB.append(row.end);
          qtyB.append(row.quantity_override);
          typeB.append(row.override_type);
          editedByB.append(row.edited_by);
          editedAtB.append(row.edited_at);
        }

        const vectors = {
          group_level: groupLevelB.finish().toVector(),
          group_value: groupValueB.finish().toVector(),
          start: startB.finish().toVector(),
          end: endB.finish().toVector(),
          quantity_override: qtyB.finish().toVector(),
          override_type: typeB.finish().toVector(),
          edited_by: editedByB.finish().toVector(),
          edited_at: editedAtB.finish().toVector(),
        };
        const table = new Table(vectors);

        const wasmTable = WasmTable.fromIPCStream(tableToIPC(table, "stream"));
        const props = new WriterPropertiesBuilder()
          .setCompression(Compression.ZSTD)
          .build();
        const parquetBytes: Uint8Array = writeParquet(wasmTable, props);

        // ── Presigned upload (bypasses WAF body limit), then save mutation. ──
        const urlResult = await apolloClient.query({
          query: GetMyDemandPlanLayerUploadUrlDocument,
          fetchPolicy: "no-cache",
        });
        const credentials = urlResult.data?.getMyDemandPlanLayerUploadUrl;
        if (!credentials) {
          throw new Error("Could not get presigned upload URL for demand plan layer");
        }
        const { uploadUrl, s3Key } = credentials;
        const putResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: parquetBytes,
        });
        if (!putResponse.ok) {
          throw new Error(
            `Failed to upload demand plan layer to S3: ${putResponse.status} ${putResponse.statusText}`
          );
        }

        const [settingsName] =
          runToUse.runSettings.settingsNameAndVersion.split("|");

        const runSettings: UserRunSettingsInput = {
          inputId: runToUse.runSettings.inputId,
          workflowId: runToUse.runSettings.workflowId,
          settingsName,
          runAttemptId: runToUse.runSettings.runAttemptId,
        };

        await saveDemandPlanLayer({
          variables: { s3Key, runSettings, runName },
        });

        setEditMode(false);
      } catch (error: unknown) {
        setJustTriggeredRun(null);
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: "Failed to save demand plan layer.",
        });
        console.error("Failed to save demand plan layer:", error);
      }
    },
    [
      saveDemandPlanLayer,
      mainRun,
      lastSuccessfulRun,
      finalForecastInputs,
      addSystemMessage,
      search,
      wasmInitialized,
      wasmError,
      whoIAm,
      setEditMode,
      apiService,
      engineInProgress,
      justTriggeredRun,
      setJustTriggeredRun,
      loading,
      runsLoading,
    ]
  );

  const value = {
    plannerOverrideInputs,
    finalForecastInputs,
    yoyPercentageInputs,
    editMode,
    loading,
    setPlannerOverrideInputs,
    setFinalForecastInputs,
    setYoYPercentageInputs,
    setEditMode,
    saveEditsAndTriggerRun,
    discardChanges,
  };

  return (
    <DemandPlanEditsContext.Provider value={value}>
      {children}
    </DemandPlanEditsContext.Provider>
  );
};
